import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTabletMode } from '../context/TabletModeContext';
import { Sparkles, BookOpen } from 'lucide-react';

/* ─────────── i18n copy ─────────── */
const COPY = {
  cat: {
    title: 'Com vols comen\u00e7ar?',
    subtitle: 'Tria la teva experi\u00e8ncia.',
    guided_title: 'Que et guiem',
    guided_sub: 'El nostre cambrer digital et recomana el millor de la casa.',
    menu_title: 'Veure la carta',
    menu_sub: 'Mira tot el men\u00fa al teu ritme.',
    badge: 'Recomanat',
  },
  es: {
    title: '\u00bfC\u00f3mo quieres empezar?',
    subtitle: 'Elige tu experiencia.',
    guided_title: 'Que te guiemos',
    guided_sub: 'Nuestro camarero digital te recomienda lo mejor de la casa.',
    menu_title: 'Ver la carta',
    menu_sub: 'Explora todo el men\u00fa a tu ritmo.',
    badge: 'Recomendado',
  },
  en: {
    title: 'How would you like to start?',
    subtitle: 'Choose your experience.',
    guided_title: 'Let us guide you',
    guided_sub: 'Our digital waiter will recommend our finest picks.',
    menu_title: 'Browse the menu',
    menu_sub: 'Explore the full menu at your own pace.',
    badge: 'Recommended',
  },
  fr: {
    title: 'Comment voulez-vous commencer\u00a0?',
    subtitle: 'Choisissez votre exp\u00e9rience.',
    guided_title: 'Laissez-vous guider',
    guided_sub: 'Notre serveur digital vous recommande nos meilleurs choix.',
    menu_title: 'Voir la carte',
    menu_sub: 'Explorez toute la carte \u00e0 votre rythme.',
    badge: 'Recommand\u00e9',
  },
};

const T = {
  bg: '#161514', bgEl: '#22201F', border: '#33302E',
  gold: '#C5A059', text: '#F9F6F0', textSec: '#A39F98',
  font: { h: "'Playfair Display', serif", b: "'DM Sans', sans-serif" },
};

export const EntryChoice = ({ onChooseMenu }) => {
  const navigate = useNavigate();
  const { tableNumber } = useParams();
  const { language } = useLanguage();
  const { isTablet } = useTabletMode();
  const t = COPY[language] || COPY.es;

  const handleGuided = () => {
    sessionStorage.setItem('pont10_entry_choice', 'guided');
    navigate(tableNumber ? `/mesa/${tableNumber}/guiado` : `/menu/guiado`);
  };

  const handleMenu = () => {
    sessionStorage.setItem('pont10_entry_choice', 'menu');
    if (onChooseMenu) onChooseMenu();
  };

  return (
    <div data-testid="entry-choice-screen" style={{
      minHeight: '100dvh', background: T.bg, position: 'relative', overflow: 'hidden',
    }}>
      {/* Subtle background image */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <img
          src="https://images.unsplash.com/photo-1633737320463-6daf601b55a5?w=900&q=60"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18 }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at center, ${T.bg}cc 0%, ${T.bg} 75%)`,
        }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1, maxWidth: isTablet ? 600 : 420, margin: '0 auto',
        padding: isTablet ? '0 40px' : '0 24px', minHeight: '100dvh',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isTablet ? 56 : 36, animation: 'fadeInUp 0.5s ease 0.1s both' }}>
          <p style={{
            fontFamily: T.font.b, fontSize: isTablet ? 14 : 12, fontWeight: 500, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: T.gold, marginBottom: 16, margin: 0,
          }}>
            Pont 10 &middot; Besal&uacute;
          </p>
          <h1 style={{
            fontFamily: T.font.h, fontSize: isTablet ? 48 : 32, fontWeight: 500, color: T.text,
            lineHeight: 1.15, letterSpacing: '-0.02em', margin: isTablet ? '20px 0 14px' : '14px 0 10px',
          }}>
            {t.title}
          </h1>
          <p style={{
            fontFamily: T.font.b, fontSize: isTablet ? 19 : 15, color: T.textSec, lineHeight: 1.5, margin: 0,
          }}>
            {t.subtitle}
          </p>
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isTablet ? 18 : 14, animation: 'fadeInUp 0.5s ease 0.2s both' }}>
          {/* Primary: Guided */}
          <button
            data-testid="entry-choice-guided"
            onClick={handleGuided}
            style={{
              position: 'relative', width: '100%', padding: isTablet ? '32px 28px' : '22px 22px',
              borderRadius: isTablet ? 26 : 20, cursor: 'pointer', textAlign: 'left',
              background: `linear-gradient(135deg, ${T.gold} 0%, #D4AF37 100%)`,
              border: `1px solid ${T.gold}`,
              boxShadow: `0 12px 30px rgba(197,160,89,0.28), inset 0 1px 0 rgba(255,255,255,0.18)`,
              display: 'flex', alignItems: 'center', gap: isTablet ? 22 : 16,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
            onTouchStart={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onTouchEnd={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{
              width: isTablet ? 64 : 48, height: isTablet ? 64 : 48, flexShrink: 0,
              borderRadius: '50%', background: 'rgba(0,0,0,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={isTablet ? 28 : 22} color={T.bg} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-block', padding: isTablet ? '4px 12px' : '3px 10px', borderRadius: 999,
                background: 'rgba(0,0,0,0.22)', marginBottom: isTablet ? 8 : 6,
                fontSize: isTablet ? 11 : 10, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: T.bg, fontFamily: T.font.b,
              }}>
                {t.badge}
              </div>
              <p style={{
                fontFamily: T.font.h, fontSize: isTablet ? 28 : 20, fontWeight: 600, color: T.bg,
                margin: 0, lineHeight: 1.2,
              }}>
                {t.guided_title}
              </p>
              <p style={{
                fontFamily: T.font.b, fontSize: isTablet ? 16 : 13, color: 'rgba(0,0,0,0.7)',
                lineHeight: 1.4, margin: '4px 0 0',
              }}>
                {t.guided_sub}
              </p>
            </div>
          </button>

          {/* Secondary: Browse menu */}
          <button
            data-testid="entry-choice-menu"
            onClick={handleMenu}
            style={{
              width: '100%', padding: isTablet ? '30px 28px' : '20px 22px',
              borderRadius: isTablet ? 26 : 20, cursor: 'pointer',
              textAlign: 'left',
              background: T.bgEl, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', gap: isTablet ? 22 : 16,
              transition: 'all 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
            onTouchStart={e => { e.currentTarget.style.background = '#2A2622'; e.currentTarget.style.borderColor = `${T.gold}66`; }}
            onTouchEnd={e => { e.currentTarget.style.background = T.bgEl; e.currentTarget.style.borderColor = T.border; }}
            onMouseEnter={e => { e.currentTarget.style.background = '#2A2622'; e.currentTarget.style.borderColor = `${T.gold}66`; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.bgEl; e.currentTarget.style.borderColor = T.border; }}
          >
            <div style={{
              width: isTablet ? 60 : 44, height: isTablet ? 60 : 44, flexShrink: 0,
              borderRadius: '50%', background: `${T.gold}18`, border: `1px solid ${T.gold}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BookOpen size={isTablet ? 26 : 20} color={T.gold} strokeWidth={1.7} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{
                fontFamily: T.font.h, fontSize: isTablet ? 26 : 18, fontWeight: 500, color: T.text,
                margin: 0, lineHeight: 1.2,
              }}>
                {t.menu_title}
              </p>
              <p style={{
                fontFamily: T.font.b, fontSize: isTablet ? 16 : 13, color: T.textSec,
                lineHeight: 1.4, margin: '4px 0 0',
              }}>
                {t.menu_sub}
              </p>
            </div>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default EntryChoice;
